import store from "@/store";
import Vue from "vue";
import VueRouter from "vue-router";

import CommissionStatistics from "@/views/Commission/CommissionStatistics.vue";
import DashboardView from "@/views/Dashboard/DashboardView.vue";
import DroneView from "@/views/Drone/DroneView.vue";
import FaqView from "@/views/Faq/FaqView.vue";
import MarketingTools from "@/views/Marketing/MarketingTools.vue";
import AllTradingAccounts from "@/views/MyClients/AllTradingAccounts.vue";
import ClientTransactionHistory from "@/views/MyClients/ClientTransactionHistory.vue";
import ClientTreeLevelFive from "@/views/MyClients/ClientTreeLevelFive.vue";
import ClientTreeLevelFour from "@/views/MyClients/ClientTreeLevelFour.vue";
import ClientTreeLevelThree from "@/views/MyClients/ClientTreeLevelThree.vue";
import ClientTreeLevelTwo from "@/views/MyClients/ClientTreeLevelTwo.vue";
import MyClients from "@/views/MyClients/MyClients.vue";
import MyClientsSummary from "@/views/MyClients/MyClientsSummary.vue";
import TradingHistory from "@/views/MyClients/TradingHistory.vue";
import OtpConfirm from "@/views/Profile/OtpConfirm.vue";
import ProfileView from "@/views/Profile/ProfileView.vue";
import TermsAndCondition from "@/views/TermsAndCondition/TermsAndCondition.vue";
import ClientsLevel from "@/views/TradeMaster/ClientsLevel.vue";
import TradeMaster from "@/views/TradeMaster/TradeMaster.vue";
import TradeMasterDetails from "@/views/TradeMaster/TradeMasterDetails.vue";
import WithdrawDetails from "@/views/Transactions/BankTransfer/WithdrawDetails.vue";
import TransferForm from "@/views/Transactions/InternalTransfer/TransferForm.vue";
import TransactionHistory from "@/views/Transactions/TransactionHistory.vue";
import WithdrawView from "@/views/Transactions/WithdrawView.vue";
import WithdrawBalance from "@/views/Transactions/TransferTogether/WithdrawBalance.vue";

Vue.use(VueRouter);

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated') {
      throw err;
    }
  });
};

// Helper function to ensure a URL has a protocol
function ensureAbsoluteUrl(url) {
  // Return null if url is not provided
  if (!url) return null;

  // Check if the URL already has a protocol (http:// or https://)
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // If no protocol, prepend http://
  return `http://${url}`;
}

// Safe getter for client portal URL
// Safe getter for client portal URL
function getClientPortalUrl() {
  // First try to get from store
  let url = store.state.publicinfo?.info?.client_portal_url;

  // If not in store, try to get from localStorage
  if (!url) {
    try {
      const storedInfo = localStorage.getItem('loginPageInfo');
      if (storedInfo) {
        const parsedInfo = JSON.parse(storedInfo);
        url = parsedInfo.client_portal_url;
      }
    } catch (error) {
      console.error("Error retrieving client_portal_url from localStorage:", error);
    }
  }

  return ensureAbsoluteUrl(url);
}

function checkUserAuthentication() {
  const token = store.state.authentication.token;

  if (token) {
    const decodedToken = decodeJwt(token);

    if (
      decodedToken &&
      new Date(decodedToken.exp * 1000) > new Date() &&
      new Date(decodedToken.exp * 1000) <
      new Date(new Date().getTime() + 24 * 60 * 60 * 1000)
    ) {
      return true;
    }
  }

  return false;
}

function decodeJwt(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64));
    return payload;
  } catch (error) {
    return null;
  }
}

const routes = [
  {
    path: "/",
    name: "dashboard",
    component: DashboardView,
    meta: { requiresAuth: true, title: "Dashboard View" },
  },

  {
    path: "/withdraw-view",
    name: "WithdrawView",
    component: WithdrawView,
    meta: { requiresAuth: true, title: "Withdraw View" },
  },

  {
    path: "/transaction-history",
    name: "TransactionHistory",
    component: TransactionHistory,
    meta: { requiresAuth: true, title: "Transaction History" },
  },

  {
    path: "/bank-transfer",
    name: "WithdrawDetails",
    component: WithdrawDetails,
    meta: { requiresAuth: true, title: "Withdraw Details" },
  },

  {
    path: "/ib-commission-withdraw",
    name: "WithdrawBalance",
    component: WithdrawBalance,
    meta: { requiresAuth: true, title: "Withdraw Balance" },
  },

  {
    path: "/otp-confirm",
    name: "OtpConfirm",
    component: OtpConfirm,
    meta: { requiresAuth: true, title: "OTP Confirm" },
  },

  {
    path: "/transfer-form",
    name: "TransferForm",
    component: TransferForm,
    meta: { requiresAuth: true, title: "Transfer Form" },
  },

  {
    path: "/profile-view",
    name: "ProfileView",
    component: ProfileView,
    meta: { requiresAuth: true, title: "Profile View" },
  },

  {
    path: "/faq",
    name: "FaqView",
    component: FaqView,
    meta: { requiresAuth: true, title: "FAQ" },
  },

  {
    path: "/client-tree",
    name: "MyClients",
    component: MyClients,
    meta: { requiresAuth: true, title: "Client Tree" },
  },

  {
    path: "/client-tree-level-two",
    name: "ClientTreeLevelTwo",
    component: ClientTreeLevelTwo,
    meta: { requiresAuth: true, title: "Client Tree Level Two" },
  },

  {
    path: "/client-tree-level-three",
    name: "ClientTreeLevelThree",
    component: ClientTreeLevelThree,
    meta: { requiresAuth: true, title: "Client Tree Level Three" },
  },

  {
    path: "/client-tree-level-four",
    name: "ClientTreeLevelFour",
    component: ClientTreeLevelFour,
    meta: { requiresAuth: true, title: "Client Tree Level Four" },
  },

  {
    path: "/client-tree-level-five",
    name: "ClientTreeLevelFive",
    component: ClientTreeLevelFive,
    meta: { requiresAuth: true, title: "Client Tree Level Five" },
  },

  {
    path: "/all-trading-accounts",
    name: "AllTradingAccounts",
    component: AllTradingAccounts,
    meta: { requiresAuth: true, title: "All Trading Accounts" },
  },

  {
    path: "/client-transaction-history",
    name: "ClientTransactionHistory",
    component: ClientTransactionHistory,
    meta: { requiresAuth: true, title: "Client Transaction History" },
  },

  {
    path: "/client-acc-summary",
    name: "MyClientsSummary",
    component: MyClientsSummary,
    meta: { requiresAuth: true, title: "Client Acc Summary" },
  },

  {
    path: "/trading-history",
    name: "TradingHistory",
    component: TradingHistory,
    meta: { requiresAuth: true, title: "Trading History" },
  },

  {
    path: "/commission-statistics",
    name: "CommissionStatistics",
    component: CommissionStatistics,
    meta: { requiresAuth: true, title: "Commission Statistics" },
  },

  {
    path: "/marketing-tools",
    name: "MarketingTools",
    component: MarketingTools,
    meta: { requiresAuth: true, title: "Marketing Tools" },
  },

  {
    path: "/terms-and-condition",
    name: "TermsAndCondition",
    component: TermsAndCondition,
    meta: { requiresAuth: true, title: "Terms And Condition" },
  },
  {
    path: "/trade-master",
    name: "TradeMaster",
    component: TradeMaster,
    meta: { requiresAuth: true, title: "Trade Master" },
  },
  {
    path: "/trade-master-details/:id",
    name: "TradeMasterDetails",
    component: TradeMasterDetails,
    meta: { requiresAuth: true, title: "Trade Master Details" },
  },
  {
    path: "/clients-level",
    name: "ClientsLevel",
    component: ClientsLevel,
    meta: { requiresAuth: true, title: "Clients Level" },
  },

  {
    path: "/drone-view",
    name: "DroneView",
    component: DroneView,
    meta: { requiresAuth: true, title: "Drone View" },
  },

  {
    path: "*",
    beforeEnter: (to, from, next) => {
      const isAuthenticated = checkUserAuthentication();
      if (!isAuthenticated) {
        // For wildcard routes, use window.location directly for external URLs
        const portalUrl = getClientPortalUrl();

        if (portalUrl) {
          window.location.href = portalUrl;
        } else {
          next("/");
        }
      } else {
        next("/");
      }
    },
  },
];

const router = new VueRouter({
  mode: "history",
  base: "/",
  routes,
});


router.beforeEach((to, from, next) => {
  const companyInfo = router.app.$store.state.company.generalInfo;
  const companyName = companyInfo ? companyInfo.company_name : null;
  document.title = `${to.meta.title || 'App'} ${companyName ? `| ${companyName}` : ""}`;

  const isAuthenticated = checkUserAuthentication();

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (isAuthenticated) {
      next();
    } else {
      const portalUrl = getClientPortalUrl();

      if (portalUrl) {
        window.location.href = portalUrl;
      } else {
        next('/');
      }
    }
  } else {
    next();
  }
});

export default router;